import type { AuthError } from "@supabase/supabase-js";
import i18n from "@/lib/i18next";

function tAuth(key: string): string {
  return i18n.t(key, { ns: "consumer" });
}

/** Resolve auth error copy via consumer i18n (login.errors.*). */
export function authErrorMessage(
  key:
    | "invalidCredentials"
    | "signOutFailed"
    | "resetEmailSent"
    | "resetEmailFailed"
    | "passwordUpdated"
    | "passwordUpdateFailed"
    | "recoveryLinkInvalid"
    | "passwordTooShort"
    | "passwordMismatch"
    | "emailNotConfirmed"
    | "emailAlreadyRegistered"
    | "passwordTooWeak"
    | "rateLimited"
    | "signupDisabled"
    | "invalidEmail"
    | "sessionExpired",
): string {
  return tAuth(`login.errors.${key}`);
}

/** @deprecated Prefer authErrorMessage — kept for call-site migration. */
export const AUTH_ERROR_MESSAGES = {
  get invalidCredentials() {
    return authErrorMessage("invalidCredentials");
  },
  get signOutFailed() {
    return authErrorMessage("signOutFailed");
  },
  get resetEmailSent() {
    return authErrorMessage("resetEmailSent");
  },
  get resetEmailFailed() {
    return authErrorMessage("resetEmailFailed");
  },
  get passwordUpdated() {
    return authErrorMessage("passwordUpdated");
  },
  get passwordUpdateFailed() {
    return authErrorMessage("passwordUpdateFailed");
  },
  get recoveryLinkInvalid() {
    return authErrorMessage("recoveryLinkInvalid");
  },
  get passwordTooShort() {
    return authErrorMessage("passwordTooShort");
  },
} as const;

function matches(error: AuthError, ...patterns: string[]): boolean {
  const msg = error.message.toLowerCase();
  const code = (error.code ?? "").toLowerCase();
  return patterns.some((p) => msg.includes(p) || code.includes(p));
}

/** Map Supabase auth errors to localized user-facing messages. */
export function translateAuthError(error: AuthError | null | undefined): string {
  if (!error) return authErrorMessage("signOutFailed");

  if (
    error.code === "invalid_credentials" ||
    matches(error, "invalid login credentials", "invalid credentials")
  ) {
    return authErrorMessage("invalidCredentials");
  }

  if (matches(error, "email not confirmed", "email_not_confirmed")) {
    return authErrorMessage("emailNotConfirmed");
  }

  if (matches(error, "user already registered", "already registered", "already exists")) {
    return authErrorMessage("emailAlreadyRegistered");
  }

  if (matches(error, "password should be at least", "weak password", "password is too weak")) {
    return authErrorMessage("passwordTooWeak");
  }

  if (matches(error, "rate limit", "too many requests", "over_email_send_rate_limit")) {
    return authErrorMessage("rateLimited");
  }

  if (matches(error, "signup disabled", "signups not allowed")) {
    return authErrorMessage("signupDisabled");
  }

  if (matches(error, "invalid email", "unable to validate email")) {
    return authErrorMessage("invalidEmail");
  }

  if (matches(error, "session expired", "refresh token", "jwt expired")) {
    return authErrorMessage("sessionExpired");
  }

  return error.message || authErrorMessage("signOutFailed");
}
